import { Check, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { STORES } from "@/lib/amazon";
import { useStore } from "@/context/StoreContext";

/**
 * Lets a reader send themselves to their own Amazon marketplace.
 * Rendered under the buy CTAs — quiet by default, obvious once opened.
 */
export const StorePicker = ({ className = "", align = "start", tone = "dark" }) => {
  const { code, store, choose } = useStore();
  const triggerTone =
    tone === "ink"
      ? "text-[hsl(220_12%_40%)] hover:text-[hsl(174_62%_28%)]"
      : "text-[hsl(215_10%_52%)] hover:text-[hsl(174_62%_64%)]";
  const valueTone =
    tone === "ink" ? "text-[hsl(220_18%_20%)]" : "text-[hsl(210_14%_78%)]";
  const hintTone =
    tone === "ink" ? "text-[hsl(220_10%_55%)]" : "text-[hsl(215_10%_40%)]";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="store-picker-trigger"
          aria-label={`Amazon store: ${store.label}. Change store`}
          className={`inline-flex items-center gap-1.5 rounded-[8px] px-1.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${triggerTone} ${className}`}
        >
          <Globe className="h-3 w-3" aria-hidden="true" />
          <span>
            Store: <span className={valueTone}>{store.label}</span>
          </span>
          <span className={hintTone}>· change</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={align}
        data-testid="store-picker-menu"
        className="max-h-[320px] w-60 overflow-y-auto border-border bg-[hsl(220_18%_8%/0.96)] backdrop-blur-md"
      >
        <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          Choose your Amazon store
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {STORES.map((s) => (
          <DropdownMenuItem
            key={s.code}
            data-testid={`store-option-${s.code}`}
            onSelect={() => choose(s.code)}
            className="cursor-pointer gap-2 text-[13px] focus:bg-[hsl(174_40%_40%/0.16)] focus:text-foreground"
          >
            <span aria-hidden="true">{s.flag}</span>
            <span className="flex-1">{s.label}</span>
            <span className="font-mono text-[10px] tracking-[0.12em] text-muted-foreground">
              .{s.tld}
            </span>
            {s.code === code && (
              <Check className="h-3.5 w-3.5 text-[hsl(174_62%_64%)]" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
